/**
 * Platform: /admin/platform. Loop-ADMIN-only. How the whole platform is doing,
 * for the people who run it, where every other screen in this product answers
 * for one person's app.
 *
 * Reads GET /api/v1/admin/metrics (cloud-api services/admin-metrics.ts, which
 * owns every definition on this page). Two of them are worth knowing here:
 * a deploy GitHub cancelled because a newer push arrived is SKIPPED, counted
 * on its own and never as a failure; and a stuck run is one still in flight
 * that nothing has touched for ten minutes.
 *
 * Same posture as GodView.tsx: the `account.isAdmin` redirect is cosmetic,
 * the real gate is server-side, and it calls apiFetch directly rather than
 * going through lib/api.ts's mock/real seam, since the nav entry is hidden in
 * mock mode.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';

import { Table, type TableColumn } from '../../components/primitives/Table';
import { Bars } from '../../components/patterns/Bars';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { SegmentedControl } from '../../components/patterns/SegmentedControl';
import { Skeleton } from '../../components/patterns/Skeleton';

import { useAccount } from '../../lib/auth/use-app-data';
import { useAsync } from '../../lib/hooks/use-async';
import { apiFetch } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';

import '../../styles/screens/admin.css';

type MetricsWindow = '24h' | '7d' | '30d';

interface CountBy {
  key: string;
  count: number;
}

interface StuckRunWire {
  run_id: string;
  project_id: string;
  project_name: string | null;
  status: string;
  updated_at: string;
}

interface RunMetricsWire {
  in_progress: number;
  stuck: number;
  succeeded: number;
  failed: number;
  duration_p50_ms: number | null;
  duration_p95_ms: number | null;
  failed_steps: CountBy[];
  failure_codes: CountBy[];
  stuck_runs: StuckRunWire[];
}

interface ReasonWire {
  source: string;
  code: string;
  count: number;
}

interface RecentFailureWire {
  id: string;
  project_id: string;
  project_name: string | null;
  commit_sha: string | null;
  source: string | null;
  code: string | null;
  detail: string | null;
  log_url: string | null;
  created_at: string;
}

interface TopProjectWire {
  project_id: string;
  project_name: string | null;
  requests: number;
  compute_units: number;
}

interface MetricsWire {
  window: MetricsWindow;
  since: string;
  generated_at: string;
  provisions: RunMetricsWire;
  teardowns: RunMetricsWire;
  deploys: {
    succeeded: number;
    failed: number;
    cancelled: number;
    success_rate: number | null;
    failures_by_reason: ReasonWire[];
    recent_failures: RecentFailureWire[];
  };
  fleet: {
    projects_production: number;
    projects_staging: number;
    accounts_total: number;
    accounts_new: number;
    waitlist_pending: number;
    custom_domains_by_status: CountBy[];
  };
  traffic: {
    days: { day: string; requests: number; cpu_ms: number; do_wall_ms: number }[];
    top_projects: TopProjectWire[];
    errors_5xx: number;
    rollup_requests: number;
  };
}

const WINDOWS: MetricsWindow[] = ['24h', '7d', '30d'];

async function loadMetrics(range: MetricsWindow): Promise<MetricsWire> {
  return apiFetch<MetricsWire>(`/api/v1/admin/metrics?window=${range}`);
}

const NUMBER = new Intl.NumberFormat();

function n(value: number): string {
  return NUMBER.format(value);
}

function pct(value: number | null): string {
  return value === null ? '-' : `${(value * 100).toFixed(1)}%`;
}

function seconds(ms: number | null): string {
  if (ms === null) return '-';
  return ms < 60_000 ? `${Math.round(ms / 1000)}s` : `${(ms / 60_000).toFixed(1)}m`;
}

function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return t('{n} min ago', { n: String(minutes) });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t('{n} h ago', { n: String(hours) });
  return t('{n} days ago', { n: String(Math.round(hours / 24)) });
}

function sourceWord(source: string | null): string {
  if (source === 'workflow') return t('GitHub Actions');
  if (source === 'platform') return t('SV Cloud');
  return t('Not recorded');
}

interface KpiProps {
  label: string;
  value: string;
  /** Spends the attention token only when the number is a problem. */
  alert?: boolean;
  note?: string;
}

function Kpi({ label, value, alert = false, note }: KpiProps) {
  return (
    <div className="plat__kpi" data-alert={alert ? 'yes' : 'no'}>
      <span className="plat__kpi-label">{label}</span>
      <span className="plat__kpi-value mono">{value}</span>
      {note ? <span className="plat__kpi-note">{note}</span> : null}
    </div>
  );
}

function CountTable({ caption, rows, keyHeader }: { caption: string; rows: CountBy[]; keyHeader: string }) {
  const columns: TableColumn<CountBy>[] = [
    { key: 'key', header: keyHeader, mono: true, render: (row) => row.key },
    { key: 'count', header: t('Count'), align: 'end', mono: true, render: (row) => n(row.count) },
  ];
  return (
    <Table
      caption={caption}
      columns={columns}
      rows={rows}
      rowKey={(row) => row.key}
      empty={<p className="plat__none">{t('None in this window.')}</p>}
    />
  );
}

export default function Platform() {
  const account = useAccount();
  const [range, setRange] = useState<MetricsWindow>('7d');
  const { data, isLoading, error } = useAsync(() => loadMetrics(range), [range]);

  useEffect(() => {
    if (account.isAdmin && data) {
      captureEvent('admin_platform_viewed', {
        window: data.window,
        provisions_in_progress: data.provisions.in_progress,
        provisions_stuck: data.provisions.stuck,
        deploys_failed: data.deploys.failed,
        deploys_skipped: data.deploys.cancelled,
      });
    }
  }, [account.isAdmin, data]);

  if (!account.isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  function changeWindow(next: MetricsWindow) {
    captureEvent('admin_platform_window_changed', { from: range, to: next });
    setRange(next);
  }

  const stuck = data ? [...data.provisions.stuck_runs, ...data.teardowns.stuck_runs] : [];

  const reasonColumns: TableColumn<ReasonWire>[] = [
    { key: 'source', header: t('From'), render: (row) => sourceWord(row.source === 'unclassified' ? null : row.source) },
    { key: 'code', header: t('Reason'), mono: true, render: (row) => row.code },
    { key: 'count', header: t('Count'), align: 'end', mono: true, render: (row) => n(row.count) },
  ];

  const failureColumns: TableColumn<RecentFailureWire>[] = [
    {
      key: 'project',
      header: t('Project'),
      render: (row) => (
        <div>
          <div>{row.project_name ?? row.project_id}</div>
          <div className="mono admin__subdomain">{row.commit_sha?.slice(0, 7) ?? ''}</div>
        </div>
      ),
    },
    {
      key: 'reason',
      header: t('Reason'),
      render: (row) => (
        <div>
          <div className="mono">{row.code ?? t('Not recorded')}</div>
          {row.detail ? <div className="plat__detail">{row.detail}</div> : null}
        </div>
      ),
    },
    { key: 'when', header: t('When'), render: (row) => ago(row.created_at) },
    {
      key: 'log',
      header: t('Log'),
      align: 'end',
      render: (row) =>
        row.log_url ? (
          <a
            href={row.log_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => captureEvent('admin_platform_log_opened', { deploy_id: row.id, code: row.code })}
          >
            {t('Open')}
          </a>
        ) : null,
    },
  ];

  const stuckColumns: TableColumn<StuckRunWire>[] = [
    { key: 'project', header: t('Project'), render: (row) => row.project_name ?? row.project_id },
    { key: 'status', header: t('Status'), mono: true, render: (row) => row.status },
    { key: 'since', header: t('Last moved'), render: (row) => ago(row.updated_at) },
    { key: 'run', header: t('Run'), mono: true, align: 'end', render: (row) => row.run_id.slice(0, 8) },
  ];

  const topColumns: TableColumn<TopProjectWire>[] = [
    { key: 'project', header: t('Project'), render: (row) => row.project_name ?? row.project_id },
    { key: 'requests', header: t('Requests'), align: 'end', mono: true, render: (row) => n(row.requests) },
    { key: 'units', header: t('Compute units'), align: 'end', mono: true, render: (row) => n(row.compute_units) },
  ];

  const days = data?.traffic.days ?? [];
  const dayValues = days.map((day) => day.requests);
  const errorRate =
    data && data.traffic.rollup_requests > 0 ? data.traffic.errors_5xx / data.traffic.rollup_requests : null;

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Platform')}</h1>
        <p className="page__lede">
          {t('How SV Cloud is doing across every account. Skipped deploys are runs GitHub cancelled because a newer push arrived, so they are not failures.')}
        </p>
      </header>

      <div className="plat__controls">
        <SegmentedControl
          label={t('Time window')}
          value={range}
          onChange={changeWindow}
          options={WINDOWS.map((value) => ({ value, label: value }))}
        />
      </div>

      {isLoading && !data && <Skeleton shape="row" count={6} label={t('Loading platform numbers')} />}

      {error && <Notice tone="attention">{t('Could not load platform numbers. Try again in a moment.')}</Notice>}

      {data && (
        <>
          <div className="plat__kpis">
            <Kpi label={t('Provisioning now')} value={n(data.provisions.in_progress)} />
            <Kpi label={t('Stuck runs')} value={n(stuck.length)} alert={stuck.length > 0} />
            <Kpi
              label={t('Provisions that failed')}
              value={n(data.provisions.failed)}
              alert={data.provisions.failed > 0}
              note={t('{n} finished', { n: n(data.provisions.succeeded) })}
            />
            <Kpi label={t('Deploy success rate')} value={pct(data.deploys.success_rate)} />
            <Kpi
              label={t('Deploys that failed')}
              value={n(data.deploys.failed)}
              alert={data.deploys.failed > 0}
              note={t('{n} went live', { n: n(data.deploys.succeeded) })}
            />
            <Kpi label={t('Deploys skipped')} value={n(data.deploys.cancelled)} note={t('Replaced by a newer push')} />
          </div>

          {stuck.length > 0 && (
            <SectionPanel title={t('Needs attention')} note={t('In flight and untouched for ten minutes or more. The five-minute sweep should close these; one that stays here means it is not.')}>
              <Table caption={t('Stuck runs')} columns={stuckColumns} rows={stuck} rowKey={(row) => row.run_id} />
            </SectionPanel>
          )}

          <SectionPanel title={t('Deploys')}>
            <h3 className="plat__sub">{t('Why deploys failed')}</h3>
            <Table
              caption={t('Why deploys failed')}
              columns={reasonColumns}
              rows={data.deploys.failures_by_reason}
              rowKey={(row) => `${row.source}:${row.code}`}
              empty={<p className="plat__none">{t('None in this window.')}</p>}
            />
            <p className="plat__footnote">
              {t('Not recorded means the deploy failed before SV Cloud started keeping reasons, on 24 September 2026.')}
            </p>
            <h3 className="plat__sub">{t('Latest failures')}</h3>
            <Table
              caption={t('Latest failures')}
              columns={failureColumns}
              rows={data.deploys.recent_failures}
              rowKey={(row) => row.id}
              empty={<p className="plat__none">{t('None in this window.')}</p>}
            />
          </SectionPanel>

          <SectionPanel title={t('Provisioning')}>
            <div className="plat__kpis">
              <Kpi label={t('Typical time to provision')} value={seconds(data.provisions.duration_p50_ms)} />
              <Kpi label={t('Slowest tenth')} value={seconds(data.provisions.duration_p95_ms)} />
              <Kpi label={t('Teardowns that failed')} value={n(data.teardowns.failed)} alert={data.teardowns.failed > 0} />
            </div>
            <h3 className="plat__sub">{t('Step that failed')}</h3>
            <CountTable caption={t('Step that failed')} rows={data.provisions.failed_steps} keyHeader={t('Step')} />
            <h3 className="plat__sub">{t('Error code')}</h3>
            <CountTable caption={t('Error code')} rows={data.provisions.failure_codes} keyHeader={t('Code')} />
          </SectionPanel>

          <SectionPanel title={t('Traffic')}>
            <div className="plat__kpis">
              <Kpi label={t('Requests')} value={n(dayValues.reduce((sum, value) => sum + value, 0))} />
              <Kpi
                label={t('Server error rate')}
                value={pct(errorRate)}
                alert={errorRate !== null && errorRate > 0.01}
                note={t('From the nightly rollup, up to a day behind')}
              />
            </div>
            {days.length > 0 ? (
              <Bars
                values={dayValues}
                max={Math.max(...dayValues)}
                rules={[]}
                tone="ink"
                markLast
                tall
                label={t('Requests per day')}
              />
            ) : (
              <EmptyState mark="box" sentence={t('No traffic in this window.')} />
            )}
            <h3 className="plat__sub">{t('Busiest apps')}</h3>
            <Table
              caption={t('Busiest apps')}
              columns={topColumns}
              rows={data.traffic.top_projects}
              rowKey={(row) => row.project_id}
              empty={<p className="plat__none">{t('None in this window.')}</p>}
            />
          </SectionPanel>

          <SectionPanel title={t('Fleet')}>
            <div className="plat__kpis">
              <Kpi label={t('Live apps')} value={n(data.fleet.projects_production)} />
              <Kpi label={t('Staging environments')} value={n(data.fleet.projects_staging)} />
              <Kpi
                label={t('Accounts')}
                value={n(data.fleet.accounts_total)}
                note={t('{n} new', { n: n(data.fleet.accounts_new) })}
              />
              <Kpi label={t('Waiting on the wait list')} value={n(data.fleet.waitlist_pending)} />
            </div>
            <h3 className="plat__sub">{t('Custom domains')}</h3>
            <CountTable
              caption={t('Custom domains')}
              rows={data.fleet.custom_domains_by_status}
              keyHeader={t('Status')}
            />
          </SectionPanel>
        </>
      )}
    </div>
  );
}
